import React, { useEffect } from 'react';

import { Form, notification } from 'antd';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import IntlMessages from 'util/IntlMessages';
import BoxContainer from 'components/BoxContainer';
import FilterContainer from 'components/FilterContainer';
import Title from 'components/BoxContainer/components/Title';
import useReportManagerDt from 'packages/utils/hooks/useReportManagerDt';
import useGetUsers from 'packages/utils/hooks/workDirection/useGetUsers';

import TitleReport from '../components/TitleReport';
import { DT_FORMS, KIND_OF_REPORTS, PATHS } from '../../constants';
import TimeTrackerFilters from '../components/TimeTrackerFilters';
import { useTimeTracker } from '../../contexts/TimeTrackerProvider';
import useTimeTrackerReportFactory from '../../hooks/useTimeTrackerReportFactory';
import ModificationsReport from '../components/reports/modifications/ModificationsReport';
import { getReportDownloadMethods } from '../utils/reportDownloadFactory';
import ReportExportButtons from '../components/ReportExportButtons';
import { ReportContext } from '../contexts/ReportContext';
import { useEmptyUserDataExportParams } from '../hooks/useEmptyUserDataExportParams';
import { useModificationReport } from '../../hooks/useModificationReport';

export default function Modifications() {
  const [preparingFile, setPreparingFile] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const intl = useIntl();
  const [form] = Form.useForm();
  const history = useHistory();
  const location = useLocation();
  const isFilterMode = location.search === '';

  const { users, loading: usersLoading, loadMoreData, hasMore } = useGetUsers();
  const {
    renderReport,
    onSubmit,
    data,
    next,
    loading: reportLoading,
    queriedPeriod,
    error: errorReport,
  } = useReportManagerDt();
  const { corporation } = useTimeTracker();
  const { data: dataParsed, error } = useTimeTrackerReportFactory({
    data: data,
    reportType: KIND_OF_REPORTS.MODIFICATIONS,
    queriedPeriod,
    reset: !renderReport,
  });

  const downloadMethods = getReportDownloadMethods(KIND_OF_REPORTS.MODIFICATIONS);
  const { emptyStateText } = useModificationReport();
  const { emptyUserDataLines } = useEmptyUserDataExportParams({
    reportType: PATHS.MODIFICATIONS,
    reportData: dataParsed,
    modificationEmptyState: emptyStateText,
    queriedPeriod,
  });

  const hasErrors = error || errorReport;
  const reportTitle = intl.formatMessage({ id: 'work.direction.modifications.report' });
  const pdfParams = {
    data: dataParsed.data,
    title: reportTitle,
    fileName: reportTitle,
    includeGlosary: false,
    orientation: 'landscape',
    emptyUserDataLines,
  };

  const onClearFilters = () => {
    form.resetFields();
    setRefresh(prev => !prev);
  };

  const onHashSubmit = () => {
    const values = form.getFieldsValue();
    const { hash } = values;

    if (!hash) {
      form.setFields([
        {
          name: 'hash',
          errors: [intl.formatMessage({ id: 'work.direction.filter.hash.validation.empty.error' })],
        },
      ]);
      return;
    }

    const currentPath = location.pathname;
    const basePath = currentPath.substring(0, currentPath.lastIndexOf('/'));
    history.push(`${basePath}/hash?value=${encodeURIComponent(hash)}`);
  };

  const mainLoading = usersLoading || reportLoading;

  useEffect(() => {
    if (renderReport && hasErrors) {
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
    }
  }, [renderReport, hasErrors, dataParsed]);

  return (
    <BoxContainer fullContent>
      <BoxContainer fixed>
        <FilterContainer
          titleClassName="gx-w-100"
          goBack={renderReport ? history.goBack : null}
          title={
            <Title.Header
              value={
                <div className="gx-flex-row gx-justify-content-between gx-align-items-center">
                  <TitleReport
                    corporationName={corporation?.name}
                    title={<IntlMessages id="work.direction.modifications.report" />}
                    rut={corporation?.RUT}
                  />
                  {renderReport && (
                    <ReportExportButtons
                      downloadMethods={downloadMethods}
                      pdfParams={pdfParams}
                      onDownloadDocx={() =>
                        downloadMethods.downloadDocx({
                          data: dataParsed,
                          fileName: 'modifications',
                          reportTitle,
                          includeGlosary: false,
                          emptyUserDataLines,
                        })
                      }
                      onDownloadExcel={() =>
                        downloadMethods.downloadExcel({
                          data: dataParsed,
                          fileName: 'modifications',
                          reportTitle,
                          includeGlosary: false,
                          emptyUserDataLines,
                        })
                      }
                      preparingFile={preparingFile}
                      setPreparingFile={setPreparingFile}
                    />
                  )}
                </div>
              }
            />
          }
          actionButtons={
            isFilterMode && [
              {
                label: <IntlMessages id="work.direction.filter.clear" />,
                action: onClearFilters,
              },
            ]
          }
        />
      </BoxContainer>
      <BoxContainer
        fullContent
        loading={mainLoading}
        onErrorCallback={history.goBack}
        error={hasErrors}
      >
        {!renderReport ? (
          <TimeTrackerFilters
            onSubmit={onSubmit}
            formName={DT_FORMS.MODIFICATIONS_REPORT}
            form={form}
            users={{
              data: users,
              onLoad: loadMoreData,
              hasMore,
              loading: usersLoading,
            }}
            refresh={refresh}
            onHashSubmit={onHashSubmit}
          />
        ) : (
          <ModificationsReport
            data={dataParsed}
            next={next}
            loading={mainLoading}
            queriedPeriod={queriedPeriod}
          />
        )}
      </BoxContainer>
    </BoxContainer>
  );
}
